import { DateTime } from 'luxon'
import { clock, feedDay } from './format'

// Лента тикета филиала (handoff 02-screens p4-p5): какие события видит филиал и как они звучат,
// группы по дням, раскладка фото, вставка новых сообщений без дублей.

/** Строка события или null - не рисуем (оценка - отдельной карточкой). */
export function eventLine (event, appealNumber) {
  const time = clock(event.createdAt)
  switch (event.type) {
    case 'closed':
    case 'solved_by_branch':
      return { text: `Обращение решено · ${time}`, tone: 'green' }
    case 'taken':
      return { text: `${event.actor?.name || 'Специалист'} подключился · ${time}`, tone: 'grey' }
    case 'created':
      return { text: `Обращение №${appealNumber} создано · ${time}`, tone: 'grey' }
    case 'reopened':
      return { text: `Обращение открыто снова · ${time}`, tone: 'grey' }
    case 'auto_closed':
      return { text: `Закрыто без ответа · ${time}`, tone: 'grey' }
    default:
      return null
  }
}

/** Группы ленты по дням в порядке времени: [{ label, items }]. */
export function groupByDay (items, now = DateTime.now()) {
  const groups = []
  for (const item of items) {
    const label = feedDay(item.createdAt, now)
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.items.push(item)
    else groups.push({ label, items: [item] })
  }

  return groups
}

// Раскладка фото в пузыре - копия fakt-support/frontend/src/shared/albumLayout.ts (таблица форматов
// 32-38), правка - сначала там. Альбом из двух и больше фото - 420 шириной, зазор 4, плитки-кропы.
// Одно фото вписывается в 420×320 по своим пропорциям без обрезки (fakt-support 1.11.0, спека
// 2026-10-20-single-photo-fit-design.md). Больше шести не показываем: шестая плитка затемнена и несёт «+N».
export const ALBUM_WIDTH = 420
export const ALBUM_GAP = 4
const MAX_TILES = 6

// Рамка одного фото: ширина альбома и 320 по высоте; масштаб не больше 1 - маленькие фото не увеличиваем.
const SINGLE_MAX_WIDTH = ALBUM_WIDTH
const SINGLE_MAX_HEIGHT = 320
// Меньше этого сторона плитки не бывает: панорама и высокий скриншот вписываются с полями, но не
// вырождаются в полоску.
const SINGLE_MIN_SIDE = 100
// Нет размеров от бэка (getimagesize не прочитал сохранённый файл) - прежняя плитка горизонтального фото.
const SINGLE_FALLBACK = { width: 320, height: 214 }

/** Плитка одного фото: { index, width, height, contain? } - contain только у крайнего формата. */
function singleTile (photo) {
  const { width, height } = photo
  if (width == null || height == null || width <= 0 || height <= 0) return { index: 0, ...SINGLE_FALLBACK }
  const scale = Math.min(SINGLE_MAX_WIDTH / width, SINGLE_MAX_HEIGHT / height, 1)
  const fitWidth = Math.round(width * scale)
  const fitHeight = Math.round(height * scale)
  // Крайний формат (скриншот строки таблицы 2372×158): по cover в плитке 420×100 пропало бы три
  // четверти ширины. Плитку держим не меньше 100, а фото в ней вписываем целиком.
  const contain = fitWidth < SINGLE_MIN_SIDE || fitHeight < SINGLE_MIN_SIDE
  return {
    index: 0,
    width: Math.max(SINGLE_MIN_SIDE, fitWidth),
    height: Math.max(SINGLE_MIN_SIDE, fitHeight),
    ...(contain && { contain }),
  }
}

/** Без размеров (бэк не прочитал их у файла) фото считаем горизонтальным - самый частый случай. */
function isVertical (photo) {
  return photo.width != null && photo.height != null && photo.height > photo.width
}

function row (indexes, width, height) {
  return indexes.map(index => ({ index, width, height }))
}

/**
 * { rows: [[{ index, width, height, contain? }]], width - ширина альбома, more - сколько спрятано за «+N» }.
 * contain - фото вписано целиком с полями (только одно фото крайнего формата), остальные режутся по cover.
 */
export function albumLayout (photos) {
  const count = photos.length
  // Вертикальная раскладка - когда вертикальны все: смешанный альбом ведёт себя как горизонтальный.
  const vertical = count > 0 && photos.every(isVertical)

  if (count === 0) return { rows: [], width: 0, more: 0 }
  if (count === 1) {
    const tile = singleTile(photos[0])
    return { rows: [[tile]], width: tile.width, more: 0 }
  }
  if (count === 2) {
    return vertical
      ? { rows: [row([0, 1], 208, 278)], width: ALBUM_WIDTH, more: 0 }
      : { rows: [row([0], 420, 140), row([1], 420, 140)], width: ALBUM_WIDTH, more: 0 }
  }
  if (count === 3) {
    return vertical
      ? { rows: [row([0, 1, 2], 137, 186)], width: ALBUM_WIDTH, more: 0 }
      : { rows: [row([0], 420, 160), row([1, 2], 208, 120)], width: ALBUM_WIDTH, more: 0 }
  }
  if (count === 4) return { rows: [row([0, 1], 208, 140), row([2, 3], 208, 140)], width: ALBUM_WIDTH, more: 0 }
  if (count === 5) return { rows: [row([0, 1], 208, 140), row([2, 3, 4], 137, 110)], width: ALBUM_WIDTH, more: 0 }

  return {
    rows: [row([0, 1, 2], 137, 110), row([3, 4, 5], 137, 110)],
    width: ALBUM_WIDTH,
    // Шестая плитка сама скрыта плашкой: «+N» - она и всё, что за ней.
    more: count > MAX_TILES ? count - MAX_TILES + 1 : 0,
  }
}

/**
 * Новое сообщение в ленту: из ответа POST и из сокета приходит одно и то же - второе не дублируем.
 * Порядок по createdAt (строки ISO с микросекундами сравниваются как строки).
 */
export function mergeItem (items, item) {
  if (items.some(i => i.kind === item.kind && i.id === item.id)) return items

  return [...items, item].sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0))
}

/** Цитата в одну строку: обрезка по слову с «…». */
export function quotePreview (text, limit = 90) {
  if (!text) return ''
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= limit) return clean
  const cut = clean.slice(0, limit)
  const space = cut.lastIndexOf(' ')

  return `${(space > 0 ? cut.slice(0, space) : cut).replace(/[.,;:!?-]+$/, '')}…`
}
